import { create } from 'zustand';

import { get as lodashGet } from 'lodash';

import { usePrescriptionStore } from './prescriptionStore';
import { PrescriberConfigurations, PrescriberService } from '../shared/prescriber';
import { ANCHORS, ANCHOR_POS, Notification } from '../components/notifications/notifications';
import { createRoot, Root } from 'react-dom/client';
import { NotificationTooltip } from '../components/notifications/NotificationTooltip';

export interface NotificationContextProps {
  checkIfComponentHasNotification(
    anchorName: ANCHORS,
    position: ANCHOR_POS,
    negativeGap: number,
    prescriberConfigurations: PrescriberConfigurations
  ): void;
  readNotification(notification: Notification): Promise<void>;
}

const rootMap = new Map<ANCHORS, Root>();
const unmountingMap = new Map<ANCHORS, boolean>();

const removeNotification = (anchorName: ANCHORS) => {
  const elementToRemove = document.getElementById(`notification-tooltip-${anchorName}`);
  const root = rootMap.get(anchorName);

  document.getElementById(`root-anchor-${anchorName}`)?.classList.remove('secondary-pulse-animation');

  if (root && !unmountingMap.get(anchorName)) {
    unmountingMap.set(anchorName, true);
    queueMicrotask(() => {
      try {
        if (rootMap.get(anchorName) === root) {
          root.unmount();
          rootMap.delete(anchorName);
        }
      } catch (e) {
      } finally {
        unmountingMap.delete(anchorName);
      }
    });
  }

  if (elementToRemove) {
    elementToRemove.remove();
  }
};

export const useNotificationStore = create<NotificationContextProps>()((set, get) => ({
  readNotification: async (notification: Notification) => {
    const prescriberConfigurations = usePrescriptionStore.getState().prescriberConfigurations;

    if (!prescriberConfigurations) return;

    try {
      await PrescriberService.readPrescriberNotification(
        prescriberConfigurations.idMedico,
        notification.notificationId
      );

      removeNotification(notification.anchorName);
    } catch (error) { }
  },
  checkIfComponentHasNotification: (anchorName: ANCHORS, position: ANCHOR_POS, negativeGap: number, prescriberConfigurations: PrescriberConfigurations) => {
    const notifications = usePrescriptionStore.getState().prescriberConfigurations?.Configuracoes?.featureNotifications;

    if (!notifications) return;

    const notification = lodashGet(notifications, `[${anchorName}]`, null);

    if (!notification) {
      removeNotification(anchorName);

      return;
    }

    if (unmountingMap.get(anchorName)) {
      return;
    }

    const existingRoot = rootMap.get(anchorName);
    const existingTooltip = document.getElementById(`notification-tooltip-${anchorName}`);
    if (existingRoot && existingTooltip) {
      const rootAnchorElement = document.getElementById(`root-anchor-${anchorName}`);
      if (rootAnchorElement) {
        rootAnchorElement.classList.add('secondary-pulse-animation');
      }
      return;
    }

    const rootAnchorElement = document.getElementById(`root-anchor-${anchorName}`);
    if (rootAnchorElement) {
      rootAnchorElement.classList.add('secondary-pulse-animation');
    }

    const renderTooltip = () => {
      const anchorElement = document.getElementById(anchorName);
      if (!anchorElement) {
        setTimeout(renderTooltip, 100);
        return;
      }

      const currentRoot = rootMap.get(anchorName);
      if (currentRoot && !unmountingMap.get(anchorName)) {
        const currentTooltip = document.getElementById(`notification-tooltip-${anchorName}`);
        if (currentTooltip) {
          return;
        }

        unmountingMap.set(anchorName, true);
        queueMicrotask(() => {
          try {
            if (rootMap.get(anchorName) === currentRoot) {
              currentRoot.unmount();
              rootMap.delete(anchorName);
            }
          } catch (e) {
          } finally {
            unmountingMap.delete(anchorName);
          }
        });
      }

      const createNewRoot = () => {
        if (unmountingMap.get(anchorName)) {
          setTimeout(createNewRoot, 10);
          return;
        }

        if (rootMap.has(anchorName)) {
          return;
        }

        const root = createRoot(anchorElement);
        rootMap.set(anchorName, root);

        root.render(
          <NotificationTooltip
            parentAnchorName={anchorName}
            position={position}
            negativeGap={negativeGap}
            onReadNotification={() => get().readNotification(notification)}
          />
        );
      };

      if (currentRoot) {
        setTimeout(createNewRoot, 10);
      } else {
        createNewRoot();
      }
    };

    if (document.getElementById(anchorName)) {
      renderTooltip();
    } else {
      requestAnimationFrame(() => {
        renderTooltip();
      });
    }
  },
}));
